/**
 * First-party API origins the dashboard may send its Firebase token to.
 *
 * BTBS-139: nexus stops serving anonymous callers, and the previous rule here
 * matched substrings of the path (`/profiles`, `/session`, `userRol`). That left
 * `/users`, `/midnps/unified`, `/finalnps/unified` and three more services out,
 * and — worse — it failed silently: a new route was born without a token and
 * nobody noticed until a screen came back empty.
 *
 * Matching by ORIGIN inverts that. Every backend of ours gets the token, so a
 * new route is covered the day it is written.
 */
const API_URL_ENV_KEYS = [
  'VITE_APP_NEXUS_URL',
  'VITE_API_URL',
  'VITE_API_SESSION_URL',
  'VITE_API_COMPANY_URL',
  'VITE_API_EVALUATORS_URL',
  'VITE_API_AUDIT_URL',
  'VITE_API_REPORTS_URL',
] as const;

/*
 * `VITE_CALENDAR_URL` is deliberately NOT here.
 *
 * Calendar requests need the Nylas provider token on top of the Firebase one,
 * and the interceptor handles them in a later branch that adds both. Including
 * the origin here would make this rule match first and send them with the
 * Firebase token alone — the calendar would authenticate and then fail to reach
 * the provider. It is not an omission; it is what keeps that branch reachable.
 */

/**
 * Extracts the origin (`scheme://host:port`) of an absolute URL.
 * Returns `null` for empty, relative or malformed values.
 */
function toOrigin(value?: string): string | null {
  if (!value) return null;

  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

let cachedOrigins: Set<string> | null = null;

/** First-party origins, derived once from the build-time env vars. */
export function getOwnApiOrigins(): Set<string> {
  if (cachedOrigins) return cachedOrigins;

  const env = import.meta.env as unknown as Record<string, string | undefined>;
  cachedOrigins = new Set(
    API_URL_ENV_KEYS.map((key) => toOrigin(env[key])).filter(
      (origin): origin is string => origin !== null,
    ),
  );

  return cachedOrigins;
}

/**
 * True when the request targets one of our own backends.
 *
 * Origins are compared parsed, never by prefix: a prefix match would also
 * accept a look-alike host such as `https://api.bonum.test.attacker.tld`, and
 * sending the user's identity token there would hand it over.
 */
export function isOwnApiUrl(url?: string): boolean {
  const origin = toOrigin(url);

  return origin !== null && getOwnApiOrigins().has(origin);
}
